<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import AddressEditForm from "./AddressEditForm.vue";

const props = defineProps({
  modalId: {
    type: String,
    required: true,
  },
  deliveries: {
    type: Array,
    default: () => [],
  },
  gireText: {
    type: String,
    default: '',
  },
  axessDisabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['send', 'changeAddress', 'getPrice']);

const activeTab = ref('address');
const isEditingAddress = ref(false);
const savingAddress = ref(false);
const selectedDelivery = ref(null);
const dismissedError = ref(false);

const visibleError = computed(() => (dismissedError.value ? '' : props.error));

const addressItems = computed(() => {
  const items = props.deliveries.filter(d => d.deliveryMethod === 'address' && (d.order_price ?? d.price) != null);
  const recalced = items.filter(d => d.recalc === true);
  return recalced.length > 0 ? recalced : items.filter(d => !d.recalc);
});

const hubItems = computed(() => props.deliveries.filter(d => d.deliveryMethod === 'hub' && (d.order_price ?? d.price) != null));

const activeItems = computed(() => (activeTab.value === 'address' ? addressItems.value : hubItems.value));

const currentAddressLine = computed(() => {
  const item = activeItems.value[0];
  return item ? `${item.address}, ${item.zip} ${item.city}` : '';
});

function selectDefaultForActiveTab() {
  selectedDelivery.value = activeItems.value[0] ?? null;
}

function formatPrice(value) {
  if (value == null) {
    return '';
  }
  return `${new Intl.NumberFormat('en-US').format(value)} NOK`;
}

function applyAddress() {
  const data = {
    address: selectedDelivery.value?.address,
    city: selectedDelivery.value?.city,
    zip: selectedDelivery.value?.zip,
    phone: selectedDelivery.value?.phone,
    contact: selectedDelivery.value?.contact,
    deliveryType: selectedDelivery.value?.deliveryType,
    deliveryId: selectedDelivery.value?.id,
    price: selectedDelivery.value?.price,
    isCustomAddress: selectedDelivery.value?.recalc ?? false,
  };
  emit('changeAddress', data);
}

function send() {
  applyAddress();
  emit('send');
}

function onSaveAddress(address) {
  savingAddress.value = true;
  emit('getPrice', { ...address, recalc: true });
}

function clear() {
  activeTab.value = 'address';
  isEditingAddress.value = false;
  savingAddress.value = false;
  selectDefaultForActiveTab();
}

watch(() => props.modalId, () => {
  clear();
});

watch(activeTab, () => {
  selectDefaultForActiveTab();
  dismissedError.value = true;
});

watch(() => props.deliveries, () => {
  selectDefaultForActiveTab();
  if (savingAddress.value && !props.error) {
    isEditingAddress.value = false;
    savingAddress.value = false;
  }
}, { deep: true });

watch(() => props.error, (val) => {
  if (val) {
    dismissedError.value = false;
    if (savingAddress.value) {
      savingAddress.value = false;
    }
  }
});

onMounted(() => {
  clear();
});
</script>

<template>
  <div
    :id="modalId"
    class="modal fade"
    tabindex="-1"
    :aria-labelledby="modalId + 'Label'"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ isEditingAddress ? trans('order_deliveries.modal.edit_address_title') : trans('order_deliveries.modal.choose_delivery_method') }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-dismiss="modal"
            aria-label="Close"
            @click="clear"
          />
        </div>
        <div class="modal-body">
          <div class="v2-tabs d-flex mb-3">
            <button
              type="button"
              class="v2-tab"
              :class="{ active: activeTab === 'address' }"
              :disabled="isEditingAddress"
              @click="activeTab = 'address'"
            >
              {{ trans('order_deliveries.modal.tab_address') }}
            </button>
            <button
              type="button"
              class="v2-tab"
              :class="{ active: activeTab === 'hub' }"
              :disabled="isEditingAddress || hubItems.length === 0"
              @click="activeTab = 'hub'"
            >
              {{ trans('order_deliveries.modal.tab_hub') }}
            </button>
          </div>

          <address-edit-form
            v-if="isEditingAddress"
            :error="error"
            @save="onSaveAddress"
            @cancel="isEditingAddress = false; dismissedError = true"
          />

          <div
            v-if="!!visibleError && !isEditingAddress"
            class="alert alert-danger"
            role="alert"
          >
            {{ visibleError }}
          </div>
          <div v-if="activeTab === 'address' && currentAddressLine && !isEditingAddress" class="mb-3">
            <label class="form-label">{{ trans('order_deliveries.modal.delivery_address_label') }}</label>
            <div class="v2-address-line d-flex justify-content-between align-items-center border rounded px-3 py-2">
              <span>{{ currentAddressLine }}</span>
              <button
                type="button"
                class="btn btn-link p-0"
                :aria-label="trans('order_deliveries.modal.edit_address_title')"
                @click="isEditingAddress = true"
              >
                <i class="fas fa-pencil" />
              </button>
            </div>
          </div>

          <div :class="{ 'v2-disabled-section': isEditingAddress }">
            <template v-if="activeItems.length > 0">
              <div class="d-md-flex flex-wrap justify-content-md-between">
                <div>{{ trans(activeTab === 'address' ? 'order_deliveries.modal.delivery_type_label' : 'order_deliveries.modal.hub_label') }}</div>
                <div>{{ trans(activeTab === 'address' ? 'order_deliveries.modal.delivery_costs_label' : 'order_deliveries.modal.hub_costs_label') }}</div>
              </div>
              <hr>
              <template
                v-for="delivery in activeItems"
                :key="delivery.deliveryType + delivery.id"
              >
                <div
                  class="v2-option form-check d-md-flex flex-nowrap justify-content-md-between"
                  :class="{ 'v2-selected': selectedDelivery === delivery }"
                  @click="selectedDelivery = delivery"
                >
                  <div class="v2-option-info">
                    <input
                      :id="delivery.deliveryType + delivery.id"
                      v-model="selectedDelivery"
                      class="form-check-input"
                      type="radio"
                      :value="delivery"
                      name="selectedDeliveryV2"
                    >
                    <label class="form-check-label" :for="delivery.deliveryType + delivery.id">
                      <strong>{{ delivery.companyName }}</strong>
                      <div>{{ delivery.address }}, {{ delivery.zip }} {{ delivery.city }}</div>
                      <div v-if="delivery.duration">{{ delivery.duration }}</div>
                      <div>
                        Tel. <span :style="!delivery?.phone ? 'color: red' : ''">{{ !delivery?.phone ? trans('order_deliveries.modal.empty_phone') : delivery?.phone }}</span>
                      </div>
                    </label>
                  </div>
                  <h5 class="mt-2 blue-text v2-option-price">
                    {{ formatPrice(delivery.order_price ?? delivery.price) }}
                  </h5>
                </div>
              </template>
            </template>
            <template v-else>
              <div>{{ trans('order_deliveries.modal.not_possible') }}</div>
            </template>
          </div>
        </div>

        <div class="modal-footer d-block text-center">
          <button
            data-dismiss="modal"
            data-toggle="modal"
            data-target="#infoWindow"
            type="submit"
            class="btn btn-lg dark-button extra-rounded btn-block"
            :disabled="!selectedDelivery || isEditingAddress"
            @click="send"
          >
            {{ trans(activeTab === 'address' ? 'order_deliveries.modal.order_delivery_button' : 'order_deliveries.modal.order_hub_button') }}
          </button>
          <a
            class="blue-text small d-block mt-2"
            :class="{ 'v2-disabled-link': isEditingAddress }"
            href="/info/levering"
            target="_blank"
          >
            {{ trans(activeTab === 'address' ? 'order_deliveries.modal.read_more_delivery' : 'order_deliveries.modal.read_more_hub') }}
          </a>
        </div>
      </div>
    </div>
  </div>

  <div
    id="infoWindow"
    class="modal fade"
    tabindex="-1"
    role="dialog"
    aria-labelledby="infoWindowTitle"
    aria-hidden="true"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-lg"
      role="document"
    >
      <div class="modal-content">
        <div class="modal-header">
          <button
            type="button"
            class="btn-close"
            data-dismiss="modal"
            aria-label="Lukk"
          />
        </div>
        <div class="modal-body text-success">
          {{ trans('order_deliveries.modal.info_1') }}
          <br>
          {{ trans('order_deliveries.modal.info_2') }}
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-success"
            data-dismiss="modal"
          >
            {{ trans('order_deliveries.modal.ok') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.v2-tabs {
  background: #f1f1f1;
  border-radius: 10px;
  padding: 4px;
}
.v2-address-line {
  min-height: 46px;
}
.v2-tab {
  flex: 1;
  border: none;
  background: transparent;
  padding: 10px;
  border-radius: 8px;
  font-weight: 600;
  color: #6c757d;
}
.v2-tab.active {
  background: #fff;
  color: #212529;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
.v2-option {
  border: 1px solid #e1e8f0;
  border-radius: 8px;
  padding: 12px 16px 12px 2em;
  margin-bottom: 10px;
}
.v2-option.v2-selected {
  background: #f1f5fd;
  border-color: #111886;
}
.v2-option-info {
  min-width: 0;
  flex: 1 1 auto;
}
.v2-option-price {
  flex-shrink: 0;
  white-space: nowrap;
  margin-left: 12px;
}
.v2-disabled-section {
  opacity: 0.5;
  pointer-events: none;
}
.v2-disabled-link {
  opacity: 0.5;
  pointer-events: none;
}
.v2-tab:disabled {
  opacity: 0.6;
}
.dark-button:disabled,
.dark-button.disabled {
  background: #111886;
  border-color: #111886;
}
.modal-footer.d-block > .btn-block {
  margin-left: 0;
  margin-right: 0;
}
.dark-button:hover {
  color: #f5f5f5;
  background-color: #0b5ed7;
  border-color: #0a58ca;
}
</style>
